import { LegalLayout } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/constants/site";

export default function Terms() {
  const email = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  return (
    <LegalLayout
      testId="terms-page"
      title="Terms of Service"
      updated="June 1, 2026"
      intro="These Terms of Use apply to the Aura Live application and any related services. By downloading or using the Application, you agree to these Terms and Conditions. Please read them carefully before using the Application."
    >
      <p>
        These Terms of Use apply to the Aura Live mobile application and any
        related services operated by Mapcast BV, a company registered in the
        Netherlands with its registered office at Herengracht 320, 1016 CE
        Amsterdam, The Netherlands ("Aura Live", the "Service Provider", "we",
        "us"). By downloading or using the Application, you agree to these Terms
        and Conditions. You should read them carefully before using the
        Application. This version is effective as of 1 June 2026.
      </p>

      <h2>License to Use the Application</h2>
      <p>
        Subject to your compliance with these Terms, the Service Provider grants
        you a limited, non-exclusive, non-transferable, revocable license to
        install and use the Application on a mobile device for personal or
        internal business purposes. You may not reproduce, distribute, modify,
        create derivative works from, reverse engineer, decompile or disassemble
        the Application, except as and only to the extent expressly permitted by
        applicable law.
      </p>

      <h2>Intellectual Property</h2>
      <p>
        The Service Provider retains all intellectual property rights in the
        Application, including its code, design, trademarks, service marks, trade
        names, logos and branding (the "IP"). Nothing in these Terms grants you
        any license or right to use the Service Provider's trademarks, logos or
        branding for any purpose. You agree not to remove, alter or obscure any
        copyright, trademark or other proprietary notices displayed in or on the
        Application. Any attempt to extract the source code, translate the
        Application into other languages, or create derivative versions is
        prohibited.
      </p>

      <h2>Eligibility & Age</h2>
      <p>
        Aura Live is a live-broadcasting service with real-money transactions. You
        represent and warrant that you are <strong>at least 18 years of age</strong>. You
        must be 18 years or older to create an account or use monetized features.
        We collect date of birth at onboarding to enforce this. If we discover you
        are under 18, we will immediately suspend your account and reverse any
        pending coin transactions. You may not use the Application through a proxy
        for a person under 18. Parents or guardians who allow a minor to use the
        Application accept full responsibility for the minor's activity.
      </p>

      <h2>In-App Coins, Gifts, Tips & Payments</h2>
      <p>Aura Live uses an in-app virtual currency called "coins" with the following rules:</p>
      <ul>
        <li><strong>Purchases:</strong> coins are purchased through Apple In-App Purchase or Google Play Billing. Apple's and Google's payment terms and refund policies govern all card and refund matters. Refund requests must be filed with Apple or Google — we cannot issue direct refunds.</li>
        <li><strong>Gifts & tips:</strong> coins spent on gifts or tips sent to hosts are final and non-refundable once sent, except where required by law.</li>
        <li><strong>Host earnings & cash-outs:</strong> hosts accumulate coins from gifts and tips. Cash-outs are subject to a minimum threshold, applicable identity verification (KYC) and Service Provider tax reporting. Cash-outs may be delayed for up to 30 days for fraud review. Coins have no monetary value until an approved cash-out is processed.</li>
        <li><strong>Chargebacks:</strong> if a coin purchase is charged back through Apple, Google or a card issuer, we may deduct the corresponding coins (including any onward-spent coins from gifts or tips) from any user's balance and, if necessary, suspend the account.</li>
        <li><strong>Taxes:</strong> hosts who cash out are responsible for any income, VAT or other tax obligations in their jurisdiction. We may collect tax information and issue tax forms where required.</li>
        <li>Coins are not transferable between users outside the gift and tip features, are not redeemable for cash except through the cash-out process, and expire if your account is inactive for 24 consecutive months.</li>
      </ul>

      <h2>Broadcasting Rules — Read Before Going Live</h2>
      <p>Live broadcasting is powerful — and carries responsibility. You represent, warrant and agree that:</p>
      <ul>
        <li>You will not broadcast minors (persons under 18) as recognizable subjects without verifiable parental consent.</li>
        <li>You have the necessary consents from any people whose voices or faces are identifiable in your broadcast. In many jurisdictions (EU, California and others) filming or recording identifiable people without consent may be illegal.</li>
        <li>You will not broadcast from private property you do not own, occupy or have express permission to enter.</li>
        <li>You will not broadcast from sensitive locations including schools, courtrooms, hospitals, military bases, security-sensitive government facilities, private residences of non-consenting individuals, or any location where local law prohibits recording.</li>
        <li>You will not broadcast criminal activity in progress, incitement to violence, self-harm content, or any content that endangers you, your subjects or your viewers.</li>
        <li>You will not broadcast sexually explicit content, nudity, or content that sexualizes minors, ever.</li>
        <li>You will not use Aura Live to stalk, harass, dox or harm any person.</li>
        <li>You will comply with all local laws including recording, privacy, broadcasting and public-decency laws in the jurisdiction where you broadcast.</li>
        <li>You retain ownership of your streams; by broadcasting you grant Aura Live and its viewers a non-exclusive right to receive, decode and display your stream in real time.</li>
      </ul>
      <p>Violation of any of the above may result in immediate account termination, forfeiture of accumulated coins, and reporting to law enforcement where required by law.</p>

      <h2>User-Generated Content & Acceptable Use</h2>
      <p>If this Application allows users to post, share or upload content, you agree not to post content that:</p>
      <ul>
        <li>Is illegal or violates third-party intellectual property rights (copyright, trademark, patents)</li>
        <li>Is abusive, threatening, harassing, defamatory or hate speech</li>
        <li>Contains discrimination or incitement to violence or illegal activity</li>
        <li>Is spam, phishing or contains malware</li>
        <li>Violates the privacy or personal-data rights of others</li>
        <li>Is misleading, false or deceptive</li>
        <li>Contains explicit violence or sexual content</li>
      </ul>
      <p>
        The Service Provider reserves the right to remove or disable access to
        content that violates these guidelines, suspend or terminate accounts of
        repeat violators, cooperate with law enforcement where illegal content is
        reported, and moderate, filter or hide content that violates these Terms
        or applicable law. If you believe content violates these Terms, infringes
        your rights or is unlawful, you may report it to the Service Provider at{" "}
        {email}, or use in-app report, block and mute tools where available. Users
        affected by moderation decisions may contact us to request further review,
        and we will respond within a reasonable period with the reasons for any
        upheld decision, subject to applicable law.
      </p>
      <p>
        By submitting User-Generated Content you grant the Service Provider a
        non-exclusive, worldwide, royalty-free license to use, reproduce,
        distribute, prepare derivative works of, display and perform the content
        in connection with the Application and the Service Provider's business.
        This license does not grant the right to sell or sublicense your content
        to third parties independently of the Application. You represent and
        warrant that you own or control all rights in the content you post.
        Processing of personal data related to User-Generated Content is governed
        by the Privacy Policy. Do not post personal data of others without their
        consent.
      </p>

      <h2>Recording, Redistribution & Screenshots</h2>
      <p>
        Live broadcasts are provided to viewers on a watch-only basis. You agree
        not to record, screen-capture, download, transcribe or redistribute
        another user's live broadcast without their explicit consent. Where you
        receive such consent, you remain solely responsible for any downstream
        use. Aura Live does not retain broadcast recordings by default; if we
        introduce optional recording in the future, it will require the host's
        affirmative opt-in and be disclosed under a supplementary agreement.
      </p>

      <h2>Your Responsibilities & Service Availability</h2>
      <p>
        It is your responsibility to maintain the security of your mobile device
        and access to the Application. We strongly advise against jailbreaking or
        rooting your device, which could expose it to malware and may cause the
        Application to malfunction. Some functions require an active internet
        connection (Wi-Fi or mobile data); we cannot be held responsible if the
        Application does not function at full capacity due to lack of connectivity
        or an exhausted data allowance. Your mobile provider's terms still apply,
        and you accept responsibility for any data or roaming charges. If you are
        not the bill payer, we assume you have the bill payer's permission. It is
        also your responsibility to keep your device charged.
      </p>
      <p>
        The Service Provider may modify the Application or charge for its services
        at any time; any charges will be clearly communicated. Nothing in these
        Terms shall limit any rights you have under applicable consumer protection
        laws that cannot be lawfully excluded.
      </p>

      <h2>Termination</h2>
      <p>
        The Service Provider may suspend your access if you materially breach
        these Terms, with written notice and, where the breach is capable of cure,
        14 days to remedy it. We may suspend or terminate access immediately
        without notice if you violate applicable law, infringe intellectual
        property rights, or engage in activity that could harm other users or the
        Service Provider. Upon termination, your right to use the Application ends
        and you must delete all copies from your devices.
      </p>

      <h2>Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, the Service Provider shall not be
        liable for any indirect, incidental, special, consequential or punitive
        damages, including lost profits, data loss or business interruption, even
        if advised of the possibility of such damages. However, the Service
        Provider retains full liability for death or personal injury caused by
        negligence, fraud or fraudulent misrepresentation, and any other liability
        that cannot be excluded under applicable law.
      </p>
      <p>
        To the fullest extent permitted by law, the Service Provider's total
        liability for any claim shall not exceed the amount paid by you for the
        Application in the 12 months preceding the claim, or the minimum amount
        required under applicable law, whichever is greater. If the Application is
        provided free of charge, liability is limited to the minimum amount
        permitted by applicable law. The Service Provider accepts no liability for
        loss arising from reliance on third-party information provided through the
        Application.
      </p>

      <h2>Indemnification</h2>
      <p>
        To the fullest extent permitted by law, you agree to indemnify and hold
        harmless the Service Provider, its affiliates, officers, directors,
        employees and agents from any claims, liabilities, damages, losses and
        expenses (including reasonable legal fees) arising out of or directly
        related to your breach of these Terms or your intentional misuse of the
        Application, including User-Generated Content you submit in violation of
        these Terms. This does not apply to claims arising from the Service
        Provider's own negligence, breach of these Terms, or violation of
        applicable law. In jurisdictions where consumer indemnification is
        restricted, this clause is limited to the maximum extent permitted.
      </p>

      <h2>Updates to the Application</h2>
      <p>
        The Service Provider may update the Application from time to time. System
        requirements may change, and you may need to download updates to keep
        using it. We do not guarantee that we will always update the Application or
        keep it compatible with your device's operating system version. You should
        accept updates when offered; if you do not, we may cease to support earlier
        versions. We may also cease providing the Application and terminate its use
        at any time without notice; upon termination the rights granted to you end
        and you must stop using and delete the Application.
      </p>

      <h2>Apple App Store & Google Play Additional Terms</h2>
      <p>If you obtained the Application from Apple's App Store, you acknowledge and agree that:</p>
      <ul>
        <li>These Terms are between you and Aura Live, not Apple Inc.</li>
        <li>Apple has no responsibility for the Application or its content.</li>
        <li>Apple is entitled to enforce these Terms as a third-party beneficiary.</li>
        <li>Apple has no obligation to provide maintenance, support, warranty or product-liability handling for the Application.</li>
        <li>Apple is not responsible for addressing any claims by you or a third party relating to the Application.</li>
        <li>You will not use the Application in any country subject to a U.S. Government embargo or on any U.S. Government prohibited-parties list.</li>
      </ul>
      <p>
        If you obtained the Application from Google Play, you acknowledge that
        Google Inc. and its affiliates have no responsibility for the Application,
        are not a party to these Terms, and are not a beneficiary of them.
      </p>

      <h2>Copyright Complaints (DMCA / EUCD)</h2>
      <p>If you believe content on Aura Live infringes your copyright, please send a written notice to {email} including:</p>
      <ul>
        <li>Identification of the copyrighted work claimed to have been infringed</li>
        <li>The identifier of the allegedly infringing content on Aura Live</li>
        <li>Your contact information (name, address, phone, email)</li>
        <li>A statement that you have a good-faith belief that the use is not authorized</li>
        <li>A statement, under penalty of perjury, that the information is accurate and that you are authorized to act</li>
        <li>Your physical or electronic signature</li>
      </ul>
      <p>
        We will respond within a reasonable time, may remove the content, and will
        notify the uploader, who may submit a counter-notice using the same
        address. Repeat infringers will be terminated.
      </p>

      <h2>Governing Law & Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of the Netherlands, where the
        Service Provider is established, excluding conflict-of-law rules, except
        to the extent mandatory consumer protection laws provide otherwise. Any
        dispute will be brought before the competent courts of Amsterdam, The
        Netherlands, without prejudice to any rights you may have to bring a
        claim in a court that is competent under mandatory law.
      </p>

      <h2>DSA Compliance (Digital Services Act)</h2>
      <p>If the Application is an intermediary service under the Digital Services Act (Regulation (EU) 2022/2065, "DSA"), the following apply in addition to the terms above.</p>
      <p><strong>Point of Contact:</strong> The Service Provider maintains a single point of contact for EU authorities and recipients of the service at {email}. Where established outside the EU, a legal representative has been designated under Article 13 DSA.</p>
      <p><strong>Statement of Reasons:</strong> When we restrict content, suspend or terminate an account, or limit features, we provide a clear statement of reasons including the nature of the restriction, its legal or contractual basis, and available redress, per Article 17 DSA.</p>
      <p><strong>Notice & Action:</strong> Users and third parties may submit notices of allegedly illegal content via the contact details in these Terms. We process notices promptly and diligently, with human review where required, acknowledging notices electronically per Article 16 DSA.</p>
      <p><strong>Out-of-Court Dispute Settlement:</strong> Content-moderation disputes may be submitted to a certified body under Article 21 DSA. Using this option does not affect your right to a judicial remedy.</p>
      <p><strong>Transparency Reporting:</strong> We publish periodic transparency reports per Article 24 DSA, available on request at {email}.</p>

      <h2>Severability</h2>
      <p>
        If any provision of these Terms is held invalid, illegal or unenforceable,
        it shall be modified to the minimum extent necessary to make it valid and
        enforceable, and the remaining provisions shall remain in full force and
        effect.
      </p>

      <h2>Entire Agreement</h2>
      <p>
        These Terms and Conditions, together with the Privacy Policy, constitute
        the entire agreement between you and the Service Provider concerning your
        use of the Application, superseding any prior agreements or understandings.
      </p>

      <h2>Changes to These Terms</h2>
      <p>
        The Service Provider may periodically update these Terms. You are advised
        to review this page regularly. We will notify you of changes by posting the
        new Terms on this page. Previous versions will be maintained and made
        available upon request at {email}. These Terms and Conditions are effective
        as of 1 June 2026.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions or suggestions about these Terms and Conditions,
        please contact the Service Provider at {email}, or by post at:
      </p>
      <p>
        <strong>Mapcast BV</strong><br />
        Herengracht 320<br />
        1016 CE Amsterdam, The Netherlands
      </p>
    </LegalLayout>
  );
}
